/**
 * Custom Stock Block v7.1 - Production Grade
 * 
 * v7.1 Changes:
 * ✓ Reads config from JSON instead of data-attributes
 * ✓ Cleaner, more maintainable code structure
 * ✓ Fixed out-of-stock and custom-build shipping display
 * 
 * v7.0 Features:
 * ✓ Full i18n support - uses Shopify locale (not browser)
 * ✓ Busy Period Mode - global toggle with custom text
 * ✓ Made-to-Order with Production Lead Time
 * ✓ Locale-aware date formatting
 */
(function() {
  'use strict';

  // ─────────────────────────────────────────────────────────────────
  // Locale Utilities
  // ─────────────────────────────────────────────────────────────────
  
  function getLocaleConfig(shopLocale) {
    const localeMap = {
      'de': 'de-DE', 'en': 'en-US', 'fr': 'fr-FR', 'es': 'es-ES',
      'it': 'it-IT', 'nl': 'nl-NL', 'pl': 'pl-PL', 'pt': 'pt-PT',
      'da': 'da-DK', 'sv': 'sv-SE', 'no': 'nb-NO', 'fi': 'fi-FI'
    };
    
    const locale = localeMap[shopLocale] || localeMap[shopLocale?.split('-')[0]] || 'en-US';
    
    return {
      locale,
      dateStyle: ['de', 'nl', 'pl'].includes(shopLocale) ? 'de' : 'en'
    };
  }
  
  function getShortDay(dayIndex, locale) {
    const d = new Date(2024, 0, 7 + dayIndex);
    return d.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '');
  }
  
  function getLongDay(dayIndex, locale) {
    const d = new Date(2024, 0, 7 + dayIndex);
    return d.toLocaleDateString(locale, { weekday: 'long' });
  }

  function formatDateShort(date, locale, dateStyle) {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const weekday = getShortDay(date.getDay(), locale);
    
    if (dateStyle === 'de') {
      return `${weekday}, ${day}.${month}.`;
    } else {
      const monthName = date.toLocaleDateString(locale, { month: 'short' });
      return `${weekday}, ${monthName} ${parseInt(day)}`;
    }
  }

  function formatRestockDate(dateStr, locale) {
    const d = new Date(dateStr);
    return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  }

  // ─────────────────────────────────────────────────────────────────
  // Cutoff Time Parser
  // ─────────────────────────────────────────────────────────────────
  
  function parseCutoffHour(value, fallback = 10.5) {
    if (value === null || value === undefined || value === '') return fallback;
    
    const str = String(value).trim();
    
    const timeMatch = str.match(/^(\d{1,2}):(\d{2})$/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const mins = parseInt(timeMatch[2], 10);
      if (hours >= 0 && hours <= 23 && mins >= 0 && mins <= 59) {
        return hours + mins / 60;
      }
    }
    
    const decimal = parseFloat(str);
    if (!isNaN(decimal) && decimal >= 0 && decimal <= 24) {
      return decimal;
    }
    
    console.warn('⚠️ Stock Block: Invalid cutoff "' + value + '", using default ' + fallback);
    return fallback;
  }

  // ─────────────────────────────────────────────────────────────────
  // Holiday Parser
  // ─────────────────────────────────────────────────────────────────
  
  function parseHolidays(str) {
    if (!str) return [];
    
    let normalized = str
      .replace(/&amp;#10;/g, ',')
      .replace(/&#10;/g, ',')
      .replace(/&amp;#13;/g, '')
      .replace(/&#13;/g, '')
      .replace(/\r?\n/g, ',')
      .replace(/[\s]+/g, ',');
    
    return normalized
      .split(',')
      .map(h => h.trim())
      .filter(h => /^\d{4}-\d{2}-\d{2}$/.test(h));
  }

  // ─────────────────────────────────────────────────────────────────
  // Business Day Functions
  // ─────────────────────────────────────────────────────────────────
  
  function isHoliday(date, holidays) {
    return holidays.includes(date.toISOString().split('T')[0]);
  }

  function isBusinessDay(date, holidays) {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !isHoliday(date, holidays);
  }

  function getNextBusinessDay(fromDate, skipToday, holidays) {
    const date = new Date(fromDate);
    if (skipToday) date.setDate(date.getDate() + 1);
    let safety = 30;
    while (!isBusinessDay(date, holidays) && safety-- > 0) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  function addBusinessDays(fromDate, days, holidays) {
    const date = new Date(fromDate);
    let added = 0, safety = 100;
    while (added < days && safety-- > 0) {
      date.setDate(date.getDate() + 1);
      if (isBusinessDay(date, holidays)) added++;
    }
    return date;
  }

  // ─────────────────────────────────────────────────────────────────
  // Main Block Initialization
  // ─────────────────────────────────────────────────────────────────
  
  function initStockBlock(block) {
    // Parse JSON config
    const configEl = block.querySelector('[data-stock-config]');
    if (!configEl) {
      console.error('Stock Block: Missing config');
      return;
    }
    
    let rawConfig;
    try {
      rawConfig = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Stock Block: Invalid config JSON', e);
      return;
    }

    // Get display settings from data attributes (needed for CSS)
    const style = block.dataset.style;
    
    // Build config from JSON
    const localeConfig = getLocaleConfig(rawConfig.locale);
    const holidays = parseHolidays(rawConfig.shipping.holidays || '');
    const cutoffHour = parseCutoffHour(rawConfig.shipping.cutoffHour, 10.5);
    
    if (holidays.length > 0) {
      console.log('📅 Stock Block: Holidays →', holidays);
    }
    
    // Time simulation for testing
    const urlParams = new URLSearchParams(window.location.search);
    const debugTime = urlParams.get('stockblock_time') || null;
    
    const getSimulatedNow = () => {
      if (debugTime) {
        const sim = new Date(debugTime);
        if (!isNaN(sim.getTime())) return sim;
      }
      return new Date();
    };
    
    if (debugTime) console.log('🕐 Stock Block: Time simulation →', debugTime);

    const config = {
      timezone: rawConfig.timezone,
      timezoneLabel: rawConfig.timezoneLabel,
      lowThreshold: rawConfig.thresholds.low,
      mediumThreshold: rawConfig.thresholds.medium,
      urgencyThreshold: rawConfig.thresholds.urgency,
      showQuantity: rawConfig.display.showQuantity,
      showCountdown: rawConfig.display.showCountdown,
      showDelivery: rawConfig.display.showDelivery,
      cutoffHour,
      deliveryDays: rawConfig.shipping.deliveryDays,
      busyMode: rawConfig.busyMode,
      localeConfig,
      texts: rawConfig.texts,
      variants: rawConfig.variants
    };

    const els = {
      dot: block.querySelector('[data-stock-dot]'),
      icon: block.querySelector('[data-stock-icon]'),
      iconCheck: block.querySelector('[data-icon-check]'),
      iconWarning: block.querySelector('[data-icon-warning]'),
      iconX: block.querySelector('[data-icon-x]'),
      iconClock: block.querySelector('[data-icon-clock]'),
      badge: block.querySelector('[data-stock-badge]'),
      badgeText: block.querySelector('[data-badge-text]'),
      text: block.querySelector('[data-stock-text]'),
      restockEta: block.querySelector('[data-restock-eta]'),
      restockText: block.querySelector('[data-restock-text]'),
      leadTimeEta: block.querySelector('[data-lead-time-eta]'),
      leadTimeText: block.querySelector('[data-lead-time-text]'),
      shippingContainer: block.querySelector('[data-shipping-container]'),
      shippingInfo: block.querySelector('[data-shipping-info]'),
      deliveryEstimate: block.querySelector('[data-delivery-estimate]'),
      deliveryText: block.querySelector('[data-delivery-text]')
    };

    const css = getComputedStyle(block);
    const colors = {
      high: css.getPropertyValue('--stock-color-high').trim(),
      highBg: css.getPropertyValue('--stock-color-high-bg').trim(),
      medium: css.getPropertyValue('--stock-color-medium').trim(),
      mediumBg: css.getPropertyValue('--stock-color-medium-bg').trim(),
      low: css.getPropertyValue('--stock-color-low').trim(),
      lowBg: css.getPropertyValue('--stock-color-low-bg').trim(),
      out: css.getPropertyValue('--stock-color-out').trim(),
      outBg: css.getPropertyValue('--stock-color-out-bg').trim(),
      madeToOrder: css.getPropertyValue('--stock-color-made-to-order').trim(),
      madeToOrderBg: css.getPropertyValue('--stock-color-made-to-order-bg').trim(),
      leadTime: css.getPropertyValue('--stock-color-lead-time').trim()
    };

    function getVariantData(variantId) {
      return config.variants.find(v => v.id === variantId) || config.variants[0];
    }

    function getStockStatus(variant) {
      const hasLeadTime = variant.lt && variant.lt.trim() !== '';
      
      // Custom build with lead time (e.g., BNF drones)
      if (hasLeadTime) {
        // Out of stock custom build (no parts)
        if (variant.t && variant.q <= 0 && variant.c !== 1) {
          return { status: 'out', color: colors.out, bg: colors.outBg };
        }
        // Available custom build
        return { 
          status: 'custom_build', 
          color: colors.madeToOrder, 
          bg: colors.madeToOrderBg,
          leadTime: variant.lt
        };
      }
      
      // Continue selling = always available
      if (variant.c) {
        return { status: 'made_to_order', color: colors.madeToOrder, bg: colors.madeToOrderBg };
      }
      
      // Not tracking inventory = always available
      if (!variant.t) {
        return { status: 'made_to_order', color: colors.madeToOrder, bg: colors.madeToOrderBg };
      }
      
      // Out of stock
      if (variant.q <= 0) {
        return { status: 'out', color: colors.out, bg: colors.outBg, restockDate: variant.r };
      }
      
      // Low stock
      if (variant.q <= config.lowThreshold) {
        return { status: 'low', color: colors.low, bg: colors.lowBg, qty: variant.q };
      }
      
      // Medium stock
      if (variant.q <= config.mediumThreshold) {
        return { status: 'medium', color: colors.medium, bg: colors.mediumBg, qty: variant.q };
      }
      
      // High stock
      return { status: 'high', color: colors.high, bg: colors.highBg, qty: variant.q };
    }

    function getStockText(stock) {
      switch (stock.status) {
        case 'made_to_order': 
          return config.texts.madeToOrder;
        case 'custom_build': 
          return config.texts.customBuild;
        case 'high': 
          return config.showQuantity 
            ? config.texts.highStockCount.replace('[count]', config.mediumThreshold) 
            : config.texts.inStock;
        case 'medium': 
          return config.showQuantity 
            ? config.texts.mediumStockCount.replace('[count]', stock.qty) 
            : config.texts.inStock;
        case 'low': 
          return config.showQuantity 
            ? config.texts.lowStockCount.replace('[count]', stock.qty) 
            : config.texts.lowStock;
        case 'out': 
          return config.texts.outOfStock;
        default:
          return config.texts.inStock;
      }
    }

    function updateStock(variantId, animate = false) {
      const variant = getVariantData(variantId);
      const stock = getStockStatus(variant);
      const text = getStockText(stock);
      const isLow = stock.status === 'low';
      const isUrgent = isLow && stock.qty <= config.urgencyThreshold;
      const isMadeToOrder = stock.status === 'made_to_order';
      const isCustomBuild = stock.status === 'custom_build';

      // Update dot
      if (els.dot) {
        els.dot.style.backgroundColor = stock.color;
        els.dot.style.display = style === 'dot' || style === 'outline' ? '' : 'none';
        els.dot.classList.toggle('stock-dot--outline', style === 'outline');
        els.dot.classList.toggle('stock-dot--pulse', isLow && block.dataset.pulse !== 'none');
      }

      // Update icon
      if (els.icon) {
        els.icon.style.color = stock.color;
        els.icon.style.display = style === 'icon' ? '' : 'none';
        if (els.iconCheck) els.iconCheck.style.display = ['high', 'medium'].includes(stock.status) ? '' : 'none';
        if (els.iconWarning) els.iconWarning.style.display = stock.status === 'low' ? '' : 'none';
        if (els.iconX) els.iconX.style.display = stock.status === 'out' ? '' : 'none';
        if (els.iconClock) els.iconClock.style.display = (isMadeToOrder || isCustomBuild) ? '' : 'none';
      }

      // Update badge
      if (els.badge) {
        els.badge.style.display = style === 'badge' ? '' : 'none';
        els.badge.style.backgroundColor = stock.bg;
        els.badge.style.color = stock.color;
        if (els.badgeText) els.badgeText.textContent = text;
      }

      // Update text
      if (els.text) {
        els.text.textContent = text;
        els.text.style.color = stock.color;
        els.text.classList.toggle('stock-text--urgent', isUrgent);
        if (animate && isUrgent) {
          els.text.classList.remove('stock-text--shake');
          void els.text.offsetWidth;
          els.text.classList.add('stock-text--shake');
        }
      }

      // Update restock ETA (for out of stock items)
      if (els.restockEta) {
        if (stock.status === 'out' && variant.r) {
          const formattedDate = formatRestockDate(variant.r, config.localeConfig.locale);
          if (els.restockText) {
            els.restockText.textContent = config.texts.restockEta.replace('[date]', formattedDate);
          }
          els.restockEta.style.display = '';
        } else {
          els.restockEta.style.display = 'none';
        }
      }

      // Update lead time ETA (for custom builds)
      if (els.leadTimeEta) {
        if (isCustomBuild && stock.leadTime) {
          if (els.leadTimeText) {
            els.leadTimeText.textContent = config.texts.leadTime.replace('[time]', stock.leadTime);
          }
          els.leadTimeEta.style.display = '';
        } else {
          els.leadTimeEta.style.display = 'none';
        }
      }

      // Update shipping container visibility (handled in updateShipping)
      block.classList.remove('custom-stock-block--loading');
    }

    function updateShipping() {
      if (!els.shippingInfo) return;

      const variant = getVariantData(currentVariantId);
      const stock = getStockStatus(variant);
      const isMadeToOrder = stock.status === 'made_to_order';
      const isCustomBuild = stock.status === 'custom_build';
      const isOutOfStock = stock.status === 'out';

      // Out of stock: Hide shipping completely
      if (isOutOfStock) {
        if (els.shippingContainer) {
          els.shippingContainer.style.display = 'none';
        }
        return;
      }

      // Custom builds: Hide regular shipping (lead time row handles it)
      if (isCustomBuild) {
        if (config.busyMode.customBuildEnabled) {
          const busyText = config.busyMode.customBuildText || config.texts.busyDefault;
          els.shippingInfo.innerHTML = busyText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          if (els.shippingContainer) els.shippingContainer.style.display = '';
        } else {
          if (els.shippingContainer) {
            els.shippingContainer.style.display = 'none';
          }
        }
        if (els.deliveryEstimate) {
          els.deliveryEstimate.style.display = 'none';
        }
        return;
      }

      // Check busy mode for other product types
      let busyText = '';
      if (isMadeToOrder && config.busyMode.madeToOrderEnabled) {
        busyText = config.busyMode.madeToOrderText || config.texts.busyDefault;
      } else if (config.busyMode.enabled && !isMadeToOrder) {
        busyText = config.busyMode.text || config.texts.busyDefault;
      }

      if (busyText) {
        const html = busyText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        els.shippingInfo.innerHTML = html;
        if (els.shippingContainer) els.shippingContainer.style.display = '';
        if (els.deliveryEstimate) els.deliveryEstimate.style.display = 'none';
        return;
      }
      
      // Show shipping container for regular products
      if (els.shippingContainer) {
        els.shippingContainer.style.display = '';
      }

      const now = getSimulatedNow();
      const wt = new Date(now.toLocaleString('en-US', { timeZone: config.timezone }));
      const currentHour = wt.getHours() + wt.getMinutes() / 60;
      const isWeekend = wt.getDay() === 0 || wt.getDay() === 6;
      const todayIsHoliday = isHoliday(wt, holidays);
      const canShipToday = !isWeekend && !todayIsHoliday;

      let shipsDate, html = '';
      const { locale, dateStyle } = config.localeConfig;

      const formatCountdown = (cutoff) => {
        const diff = cutoff - currentHour;
        if (diff <= 0) return null;
        const hrs = Math.floor(diff), mins = Math.floor((diff - hrs) * 60);
        if (hrs > 0 && mins > 0) return `${hrs}h ${mins}m`;
        return hrs > 0 ? `${hrs}h` : `${mins}m`;
      };

      if (!canShipToday) {
        shipsDate = getNextBusinessDay(wt, true, holidays);
        const dayName = getLongDay(shipsDate.getDay(), locale);
        html = config.texts.shipsNextDay
          .replace('[day]', dayName)
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        if (!config.texts.shipsNextDay.includes('**')) {
          html = config.texts.shipsNextDay.replace('[day]', `<strong>${dayName}</strong>`);
        }
      } else {
        const countdown = formatCountdown(config.cutoffHour);
        if (countdown) {
          shipsDate = wt;
          html = config.texts.sameDay
            .replace('[time]', `<span class="shipping-countdown">${countdown}</span>`)
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          if (!config.texts.sameDay.includes('**')) {
            html = html
              .replace(/same-day shipping/gi, '<strong>same-day shipping</strong>')
              .replace(/Versand heute/gi, '<strong>Versand heute</strong>');
          }
        } else {
          shipsDate = getNextBusinessDay(wt, true, holidays);
          const dayName = getLongDay(shipsDate.getDay(), locale);
          const tomorrow = new Date(wt);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          if (shipsDate.toDateString() === tomorrow.toDateString()) {
            html = config.texts.shipsTomorrow
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            if (!config.texts.shipsTomorrow.includes('**')) {
              html = config.texts.shipsTomorrow
                .replace(/\b(tomorrow|morgen)\b/gi, '<strong>$1</strong>');
            }
          } else {
            html = config.texts.shipsNextDay
              .replace('[day]', dayName)
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            if (!config.texts.shipsNextDay.includes('**')) {
              html = config.texts.shipsNextDay.replace('[day]', `<strong>${dayName}</strong>`);
            }
          }
        }
      }

      els.shippingInfo.innerHTML = html;

      // Update delivery estimate
      if (els.deliveryEstimate && els.deliveryText && shipsDate && config.showDelivery) {
        const dd = addBusinessDays(shipsDate, config.deliveryDays, holidays);
        const formattedDate = formatDateShort(dd, locale, dateStyle);
        els.deliveryText.innerHTML = `${config.texts.delivery} <span class="delivery-date">${formattedDate}</span>`;
        els.deliveryEstimate.style.display = '';
      }
    }

    // ─────────────────────────────────────────────────────────────────
    // Variant Detection
    // ─────────────────────────────────────────────────────────────────
    
    let currentVariantId = rawConfig.currentVariantId || 0;

    function onVariantChange(id) {
      if (!id || id === currentVariantId) return;
      currentVariantId = id;
      block.classList.add('custom-stock-block--updating');
      requestAnimationFrame(() => {
        updateStock(id, true);
        updateShipping();
        block.classList.remove('custom-stock-block--updating');
      });
    }

    // 1. Native Shopify/Theme Events
    ['variant:changed', 'variant:change', 'variantChange', 'theme:variant:change'].forEach(evt => {
      document.addEventListener(evt, (e) => {
        const id = e.detail?.variant?.id || e.detail?.id;
        if (id) onVariantChange(parseInt(id));
      });
    });

    // 2. MutationObserver on variant input
    const form = block.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        new MutationObserver(() => {
          const id = parseInt(input.value);
          if (id) onVariantChange(id);
        }).observe(input, { attributes: true, attributeFilter: ['value'] });
        
        let lastVal = input.value;
        form.addEventListener('change', () => {
          setTimeout(() => {
            if (input.value !== lastVal) {
              lastVal = input.value;
              onVariantChange(parseInt(input.value));
            }
          }, 10);
        });
      }
    }

    // 3. URL popstate
    const checkUrl = () => {
      const v = new URLSearchParams(window.location.search).get('variant');
      if (v) onVariantChange(parseInt(v));
    };
    window.addEventListener('popstate', checkUrl);
    
    // 4. URL polling
    let lastHref = window.location.href;
    const poll = setInterval(() => {
      if (window.location.href !== lastHref) {
        lastHref = window.location.href;
        checkUrl();
      }
    }, 200);

    // ─────────────────────────────────────────────────────────────────
    // Init
    // ─────────────────────────────────────────────────────────────────
    
    updateStock(currentVariantId);
    if (config.showCountdown) {
      updateShipping();
      block._shippingInterval = setInterval(updateShipping, 60000);
    }

    block._cleanup = () => {
      clearInterval(poll);
      if (block._shippingInterval) clearInterval(block._shippingInterval);
    };

    requestAnimationFrame(() => block.classList.remove('custom-stock-block--loading'));
  }

  function init() {
    document.querySelectorAll('[data-stock-block]').forEach(initStockBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
